import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  IconButton,
  TextField,
  Checkbox,
  ListItemText,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  Avatar,
  Button,
  DialogActions,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { Search, MoreVert, Minimize } from '@mui/icons-material';
import Divider from '@mui/material/Divider';
import { useNavigate } from 'react-router-dom';
import Autocomplete from '@mui/material/Autocomplete';
import { useDispatch, useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';

// Icons
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';

// Components
import ConversationList from './ConversationList';
import ChatRequestItem from './ChatRequestItem';
import Searchbar from './Searchbar';
import MinimizeIcon from './icons/MinimizeIcon';
import SendRequestIcon from './icons/SendRequestIcon';

// APIs and Services
import AdminGetUserAPIs from '../../utilities/api/workshop/AdminGetUser';
import AdminMessagingAPIs from '../../utilities/api/IMS/AdminMessagingAPIs';

// Utilities
import { notifyError, notifySuccess } from '../../utils/notifyToasts';

// Redux actions
import {
  addMessageToConversation,
  setSelectedConversation,
} from '../../redux/slices/conversationSlice';

/**
 * Sidebar Component
 *
 * Main sidebar interface that provides:
 * - Conversation management and display
 * - User search functionality with debounced API calls
 * - Chat request creation (individual and group chats)
 * - Chat request acceptance/decline interface
 * - Navigation between conversations and requests views
 *
 * Key Features:
 * - Toggle between conversations view and chat requests view
 * - Real-time user search with suggestions
 * - Group chat creation with participant selection
 * - Individual chat request sending
 * - Responsive design with custom scrollbars
 *
 * @param {string} lastLocation - Previous route for navigation back button
 * @param {Array} chatRequests - List of pending chat requests
 * @param {Function} onRequestResponse - Handler for accepting/declining requests
 */
const Sidebar = ({
  lastLocation,
  chatRequests,
  onRequestResponse,
  setView,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // ========== REDUX STATE ==========
  const { user } = useSelector(state => state.user.user);
  const { conversations } = useSelector(state => state.conversation);
  const dispatch = useDispatch();

  // ========== COMPONENT STATE ==========
  // Search and user management
  const [userSuggestions, setUserSuggestions] = useState([]); // Search suggestions for conversation lookup
  const [searchQuery, setSearchQuery] = useState(''); // Current search input
  const [users, setUsers] = useState([]); // Available users for chat requests

  // UI state management
  const [showConversations, setShowConversations] = useState(true); // Toggle between conversations/requests view
  const [showRequestDialog, setShowRequestDialog] = useState(false); // Control new chat dialog visibility
  const [createGroup, setCreateGroup] = useState(false); // Toggle between individual/group chat creation

  // Chat request management
  const [sendRequestsTo, setSendRequestsTo] = useState([]); // Selected users for new chat request

  // References
  const groupNameRef = useRef(); // Reference to group name input field
  const navigate = useNavigate();

  // ========== USER SEARCH AND SUGGESTIONS ==========
  /**
   * Fetches users available for chat requests
   *
   * @param {Event} event - Triggering event (optional)
   * @param {string} query - Search query for filtering users (default: "")
   *
   * User Filtering Logic:
   * 1. Fetch users based on search query
   * 2. Filter by team (same TL_name as current user)
   * 3. Exclude current user
   * 4. For individual chats: exclude users with existing conversations
   * 5. For group chats: include all eligible users
   */
  const fetchUsers = async (event, query = '') => {
    try {
      // Fetch users from API (with search query if provided)
      let fetchedUsers = await AdminGetUserAPIs.UserDetailsGet(query);

      // Filter users: same team, exclude self
      fetchedUsers = fetchedUsers.filter(
        fetchedUser =>
          fetchedUser.username !== user.username &&
          fetchedUser.TL_name === user.TL_name
      );

      // For individual chats: exclude users with existing conversations
      if (!createGroup) {
        const conversationUserNames = conversations.map(conv => conv.userName);

        fetchedUsers = fetchedUsers.filter(
          fetchedUser => !conversationUserNames.includes(fetchedUser.username)
        );
      }

      setUsers(fetchedUsers);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  // ========== CHAT REQUEST MANAGEMENT ==========
  /**
   * Handles sending chat requests (individual or group)
   *
   * Individual Chat Flow:
   * 1. Create new thread with current user as participant
   * 2. Send chat request to selected user(s)
   * 3. Show success notification and close dialog
   *
   * Group Chat Flow:
   * 1. Validate group name is provided
   * 2. Create new group thread with group name
   * 3. Send group invitation to selected participants
   * 4. Initialize conversation with empty message
   * 5. Show success notification and close dialog
   */
  const handleSendRequests = async () => {
    try {
      // Group chat creation
      if (createGroup) {
        // Validate group name
        if (!groupNameRef.current.value) {
          notifyError('Please enter a group name');
          return;
        }

        // Create group thread
        const thread = await AdminMessagingAPIs.CreateThread({
          participants: [user.username],
          group_name: groupNameRef.current.value,
          is_group_chat: true,
        });

        // Send group invitations
        await AdminMessagingAPIs.SendChatRequest(thread.id, {
          participants: sendRequestsTo,
          is_group_chat: true,
          group_name: groupNameRef.current.value,
        });

        // Initialize conversation with empty message
        dispatch(
          addMessageToConversation({
            conversationId: thread.id,
            message: {
              id: uuidv4(),
              text: '',
              date: new Date().toLocaleString([], {
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
              }),
              sender: '',
              isCurrentUser: false,
            },
          })
        );

        notifySuccess(
          'Group created successfully and chat request sent successfully'
        );
        handleClose();
        return;
      }

      // Individual chat creation
      const thread = await AdminMessagingAPIs.CreateThread({
        participants: [user.username],
      });

      await AdminMessagingAPIs.SendChatRequest(thread.id, {
        participants: sendRequestsTo,
        is_group_chat: false,
      });

      notifySuccess('Chat request sent successfully');
      handleClose();
    } catch (error) {
      console.error('Error sending chat requests:', error);
      notifyError('Error sending chat request');
    }
  };

  /**
   * Closes the new chat dialog and resets all related state
   */
  const handleClose = () => {
    setSendRequestsTo([]);
    setShowRequestDialog(false);
    setCreateGroup(false);
  };

  /**
   * Handles checkbox selection for users in the new chat dialog
   *
   * @param {string} userId - Username to toggle selection
   */
  const handleCheckboxChange = userId => {
    setSendRequestsTo(prev => {
      if (prev.includes(userId)) {
        return prev.filter(id => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };

  // ========== NAVIGATION HANDLERS ==========
  /**
   * Navigates back to the previous location
   */
  const handleMinimizeClick = () => {
    navigate(lastLocation);
  };

  /**
   * Handles search input changes
   *
   * @param {Event} event - Input change event
   * @param {string} newInputValue - New search query value
   */
  const handleInputChange = (event, newInputValue) => {
    setSearchQuery(newInputValue);
  };

  /**
   * Handles user selection from search suggestions
   *
   * @param {Event} event - Selection event
   * @param {Object} selectedUser - Selected user object with id and name
   *
   * Flow:
   * 1. Find existing conversation for selected user
   * 2. If conversation exists, select it and switch to conversation view
   * 3. If no conversation exists, could implement new conversation creation
   */
  const handleUserSelect = (event, selectedUser) => {
    if (selectedUser) {
      // Check if the selected user already has a conversation
      const existingConversation = conversations.find(
        conv => conv.name === selectedUser.name
      );
      if (existingConversation) {
        dispatch(setSelectedConversation(existingConversation.id));
      }
    }
  };

  // ========== DEBOUNCED SEARCH EFFECT ==========
  /**
   * Implements debounced search for thread suggestions
   *
   * Search Logic:
   * 1. Wait 300ms after user stops typing
   * 2. If query has content, search for matching threads
   * 3. Update suggestions with formatted results
   * 4. Clear suggestions if query is empty
   */
  useEffect(() => {
    const delayDebounce = setTimeout(async () => {
      if (searchQuery.length > 0) {
        try {
          const searchedUsers =
            await AdminMessagingAPIs.SearchThreads(searchQuery);
          // Format search results for autocomplete
          setUserSuggestions(
            searchedUsers.map(user => ({
              id: user.thread_id,
              name: user.name,
            }))
          );
        } catch (error) {
          console.error('Search error:', error);
        }
      } else {
        // Clear suggestions when search is empty
        setUserSuggestions([]);
      }
    }, 300); // 300ms debounce delay

    // Cleanup previous timer if user types again
    return () => clearTimeout(delayDebounce);
  }, [searchQuery]);

  // ========== INITIALIZATION EFFECTS ==========
  /**
   * Initial data fetch on component mount
   */
  useEffect(() => {
    fetchUsers();
  }, []);

  /**
   * Refresh users when group creation mode changes
   * This ensures the user list is updated based on filtering logic
   */
  useEffect(() => {
    fetchUsers();
  }, [createGroup]);

  // ========== RENDER ==========
  return (
    <Box
      sx={{
        backgroundColor: '#fff',
        border: isMobile ? 'none' : '1px solid #D3D3D3',
        display: 'flex',
        flexDirection: 'column',
        margin: isMobile ? '0px' : '15px',
        borderRadius: isMobile ? '0px' : '12px',
        boxShadow: '0px 4px 16px 0px #0000001A',
        height: isMobile ? '100%' : 'calc(100% - 28px)',
      }}
    >
      {/* ========== HEADER SECTION ========== */}
      <Box
        sx={{
          padding: '10px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid #A6A6A6',
        }}
      >
        {/* Title or Back Button */}
        {showConversations ? (
          <Typography variant="h6">
            <b>Messages</b>
          </Typography>
        ) : (
          <IconButton onClick={() => setShowConversations(true)}>
            <ArrowBackIosNewIcon sx={{ color: 'black' }} />
          </IconButton>
        )}

        {/* Action Buttons */}
        <Box>
          {/* Chat Requests Button */}
          <Tooltip title="Chat Requests">
            <IconButton onClick={() => setShowConversations(false)}>
              <PersonAddAltIcon sx={{ color: 'black' }} />
            </IconButton>
          </Tooltip>

          {/* Send Request Button */}
          <Tooltip title="Send Request">
            <IconButton onClick={() => setShowRequestDialog(true)}>
              <SendRequestIcon />
            </IconButton>
          </Tooltip>

          {/* New Chat Dialog */}
          {showRequestDialog && (
            <Dialog
              open={showRequestDialog}
              onClose={handleClose}
              fullWidth={true}
              maxWidth="xs"
            >
              <DialogTitle variant="h5">New Chat</DialogTitle>

              {/* Search Bar for Users */}
              <Box sx={{ padding: '0 20px' }}>
                <Searchbar
                  showSuggestions={false}
                  userSuggestions={users}
                  handleInputChange={fetchUsers}
                />
              </Box>

              <DialogContent
                sx={{
                  maxHeight: '500px',
                  overflowY: 'auto',
                  // Custom scrollbar styling
                  '&::-webkit-scrollbar': {
                    width: '6px',
                    backgroundColor: 'transparent',
                  },
                  '&::-webkit-scrollbar-track': {
                    backgroundColor: 'transparent',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: 'rgba(0,0,0,0.2)',
                    borderRadius: '3px',
                    '&:hover': {
                      backgroundColor: 'rgba(0,0,0,0.4)',
                    },
                  },
                  scrollBehavior: 'smooth',
                  '-webkit-overflow-scrolling': 'touch',
                }}
              >
                {/* Group Creation Section */}
                {createGroup ? (
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'center',
                      marginBottom: '20px',
                    }}
                  >
                    <Typography variant="h6" sx={{ marginBottom: '10px' }}>
                      Group Name
                    </Typography>
                    <TextField inputRef={groupNameRef} />
                  </Box>
                ) : (
                  /* New Group Button */
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      backgroundColor: '#F9F9F9',
                      padding: '8px',
                      border: '1px solid #E9E9E9',
                      borderRadius: '8px',
                      marginBottom: '20px',
                      cursor: 'pointer',
                    }}
                    onClick={() => setCreateGroup(true)}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <Avatar
                        src={user.profile_pic}
                        sx={{ marginRight: '10px' }}
                      />
                      <Typography variant="div" sx={{ marginRight: '10px' }}>
                        New Group
                      </Typography>
                    </Box>
                  </Box>
                )}

                {/* Instructions */}
                <DialogContentText sx={{ marginBottom: '20px' }}>
                  {createGroup
                    ? 'Select users to create group'
                    : 'Select users to send chat request'}
                </DialogContentText>

                {/* User Selection List */}
                {users.map(user => (
                  <Box
                    key={user.id}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      padding: '8px',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <Avatar
                        src={user.profile_pic}
                        sx={{ marginRight: '10px' }}
                      />
                      <Typography variant="div" sx={{ marginRight: '10px' }}>
                        {user.name}
                      </Typography>
                    </Box>
                    <Checkbox
                      checked={sendRequestsTo.includes(user.username)}
                      onChange={() => handleCheckboxChange(user.username)}
                    />
                  </Box>
                ))}
              </DialogContent>

              {/* Action Button */}
              <Button
                variant="contained"
                color="primary"
                sx={{
                  margin: '0 20px 20px 20px',
                  padding: '8px',
                }}
                onClick={handleSendRequests}
              >
                {createGroup ? 'Create Group' : 'Send Requests'}
              </Button>
            </Dialog>
          )}

          {/* Minimize Button */}
          {/* <Tooltip title="Minimize">
            <IconButton onClick={handleMinimizeClick}>
              <MinimizeIcon />
            </IconButton>
          </Tooltip> */}
        </Box>
      </Box>

      {/* ========== SEARCH SECTION ========== */}
      <Box sx={{ padding: '12px' }}>
        <Searchbar
          userSuggestions={userSuggestions}
          handleInputChange={handleInputChange}
          handleUserSelect={handleUserSelect}
        />
      </Box>

      {/* ========== CONTENT SECTION ========== */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          // Custom scrollbar styling
          '&::-webkit-scrollbar': {
            width: '3px',
            backgroundColor: 'transparent',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0,0,0,0.2)',
            borderRadius: '3px',
            '&:hover': {
              backgroundColor: 'rgba(0,0,0,0.4)',
            },
          },
          scrollBehavior: 'smooth',
          '-webkit-overflow-scrolling': 'touch',
        }}
      >
        {showConversations ? (
          /* ========== CONVERSATIONS VIEW ========== */
          <>
            {conversations &&
              conversations.length > 0 &&
              conversations.map((conv, i) => (
                <Box key={conv.id}>
                  <ConversationList
                    userName={conv.name}
                    message={conv.message}
                    date={conv.date}
                    avatar={conv.avatar}
                    onClick={() => {
                      dispatch(setSelectedConversation(conv.id));
                      setView && setView('chat');
                    }}
                  />
                  {i !== conversations?.length - 1 && (
                    <Divider variant="middle" />
                  )}
                </Box>
              ))}
          </>
        ) : (
          /* ========== CHAT REQUESTS VIEW ========== */
          <>
            {chatRequests &&
              chatRequests.length > 0 &&
              chatRequests.map(request => (
                <ChatRequestItem
                  key={request.id}
                  id={request.id}
                  sender={
                    request.group_name
                      ? request.group_name
                      : request.invited_by_username
                  }
                  avatar={null}
                  handleRequest={onRequestResponse}
                />
              ))}
          </>
        )}
      </Box>
    </Box>
  );
};

export default Sidebar;
